import { Suspense, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { THEME_COMPONENTS } from '@/themes/components';
import { ARTICLES, THEME_LABELS } from '@/data/articles';
import { THEME_REGISTRY } from '@/themes/index';
import { useThemeCarousel } from '@/contexts/ThemeCarouselContext';
import { StoryHero } from '@/components/StoryHero';

function safeHex(color: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : '#D4A843';
}

function MoreStoriesStrip({ currentId }: { currentId: string }) {
  const others = ARTICLES.filter((a) => a.id !== currentId).slice(0, 3);

  return (
    <section className="bg-[#0a0a0a] px-6 py-12 border-t border-white/10">
      <p className="text-center text-[11px] uppercase tracking-[0.3em] text-white/30 mb-8">
        More from Reporter
      </p>
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
        {others.map((article) => {
          const coverImage = article.content.hero?.coverImage ?? '';
          return (
            <Link
              key={article.id}
              to={`/story/${article.id}`}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl flex flex-col justify-end bg-black"
              style={{
                background: coverImage
                  ? undefined
                  : `linear-gradient(135deg, ${article.gradientFrom} 0%, ${article.gradientTo} 100%)`,
              }}
            >
              {coverImage && (
                <div
                  className="absolute inset-0 scale-100 group-hover:scale-[1.04] transition-transform duration-500 ease-out"
                  style={{
                    backgroundImage: `url(${coverImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="relative p-4">
                <p className="text-[10px] text-white/50 uppercase tracking-widest mb-1">
                  {THEME_LABELS[article.theme] ?? article.theme}
                </p>
                <p className="text-sm font-semibold text-white leading-tight">
                  {article.title}
                </p>
                <p className="text-[10px] text-white/40 mt-2 group-hover:text-white/70 transition-colors">
                  Read story →
                </p>
              </div>
            </Link>
          );
        })}
      </div>
      <p className="mt-8 text-center text-[11px] text-white/20">
        Reporter · 10 editorial layouts · Africa
      </p>
    </section>
  );
}

function LoadingFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-black">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/10 border-t-white/60" />
        <p className="text-sm text-white/40">Loading layout…</p>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-black px-4 text-white">
      <p className="text-6xl font-bold text-white/10">404</p>
      <p className="text-lg font-semibold">Story not found</p>
      <p className="text-sm text-white/50">
        This story doesn't exist or may have been removed.
      </p>
      <Link
        to="/"
        className="rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-black transition hover:bg-white/90"
      >
        Back to Reporter
      </Link>
    </div>
  );
}

export default function StoryPage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? ARTICLES.find((a) => a.id === id) : undefined;
  const { currentThemeId } = useThemeCarousel();

  const activeThemeId = article?.theme ?? currentThemeId;
  const accentColor = safeHex(THEME_REGISTRY[activeThemeId]?.accentColor ?? '#D4A843');

  useEffect(() => {
    if (article) {
      document.title = `${article.title} · Reporter`;
    }
    return () => {
      document.title = 'Reporter';
    };
  }, [article]);

  if (!id || !article) return <NotFound />;

  const ThemeComponent = THEME_COMPONENTS[activeThemeId] ?? THEME_COMPONENTS[article.theme];
  if (!ThemeComponent) return <NotFound />;

  const content = {
    ...article.content,
    footer: article.content.footer
      ? {
          ...article.content.footer,
          shareUrl: `${window.location.origin}/story/${article.id}`,
        }
      : article.content.footer,
  };

  // Themes that render their own cover/title and need the full viewport for
  // the immersive experience (currently: page-flip's magazine book).
  const SELF_COVERED_THEMES = new Set(['page-flip']);
  const isSelfCovered = SELF_COVERED_THEMES.has(activeThemeId);

  return (
    <div className="flex min-h-screen flex-col bg-black" style={{ paddingTop: '2.25rem' }}>
      {/* Full-bleed cinematic hero (skipped for themes with their own cover) */}
      {!isSelfCovered && <StoryHero article={article} themeId={activeThemeId} />}

      {/* Theme body with cinematic blockquote styles */}
      <div className="flex-1 story-body">
        <style>{`
          .story-body blockquote {
            border-left: 3px solid ${accentColor};
            background: rgba(255,255,255,0.04);
            padding: 1.25rem 1.5rem;
            margin: 2rem 0;
            font-style: italic;
            font-size: 1.15em;
            color: rgba(255,255,255,0.85);
            border-radius: 0 4px 4px 0;
          }
          .story-body blockquote p { margin: 0; }
        `}</style>
        <Suspense fallback={<LoadingFallback />}>
          <ThemeComponent {...content} />
        </Suspense>
      </div>

      {/* Redesigned more-stories strip */}
      <MoreStoriesStrip currentId={article.id} />
    </div>
  );
}
